import { ColDef } from 'ag-grid-community';
import { flowRight as compose, property } from 'lodash-es';

import { dateToYYYYMMDD } from '@mummssoftware/utils/dates';
import { DetailsRenderer } from './DetailsRenderer';
import { DateRenderer } from './DateRenderer';
import { TypeRenderer } from './TypeRenderer';
import { formatPatientName, formatPersonName } from './utils';

import defaults from '../../translations/en.json';

const textFilter = {
  filter: 'agTextColumnFilter',
  filterParams: {
    buttons: ['reset'],
    filterOptions: ['contains'],
    applyMiniFilterWhileTyping: false,
    suppressAndOrCondition: true,
  },
};

const Type = (types: string[]): ColDef => ({
  headerName: defaults.Type,
  width: 100,
  resizable: true,
  cellRendererFramework: TypeRenderer,
  cellRendererParams: { types },
  valueGetter: property('data.incidenttype'),
  ...textFilter,
});

const backendToDisplayDate = (d: string) => d.split(' ')[0];

const Date = (min?: Date, max?: Date): ColDef => ({
  headerName: defaults.Date,
  /** sizeColumnsToFit() respects this floor so the ISO date + picker stay readable. */
  minWidth: 128,
  width: 130,
  resizable: true,
  cellRendererFramework: DateRenderer,
  valueGetter: compose(
    backendToDisplayDate,
    property('data.incidentdatetime_utc'),
  ),
  filter: 'agDateColumnFilter',
  menuTabs: ['filterMenuTab'],
  sort: 'desc',
  filterParams: {
    comparator: (filterChoice: Date, b: string) =>
      b.localeCompare(dateToYYYYMMDD(filterChoice)),
    defaultOption: 'inRange',
    buttons: ['apply', 'reset'],
    closeOnApply: true,
    newRowsAction: 'keep',
    inRange: true,
    inRangeInclusive: true,
    equals: false,
    notEquals: false,
    lessThan: false,
    greaterThan: false,
    suppressAndOrCondition: true,
  },
});

const Reporter = (isCrossPatient: boolean): ColDef => ({
  headerName: defaults.Reporter,
  width: isCrossPatient ? 200 : 300,
  resizable: true,
  valueGetter: compose(formatPersonName, property('data')),
  ...textFilter,
});

const Patient = (smallWidget: boolean, isCrossPatient: boolean): ColDef[] => [
  {
    headerName: defaults.Patient,
    width: 150,
    resizable: true,
    valueGetter: compose(formatPatientName, property('data')),
    hide: smallWidget || !isCrossPatient,
    ...textFilter,
  },
  {
    headerName: defaults.PatientID,
    width: 80,
    resizable: true,
    field: 'patientnumber',
    hide: smallWidget || !isCrossPatient,
    ...textFilter,
  },
];

const Details = (smallWidget: boolean): ColDef => ({
  headerName: defaults.Details,
  field: 'details',
  width: 70,
  resizable: true,
  /** Avoid the column being sized to 0 when the row is short on space. */
  minWidth: 56,
  hide: smallWidget,
  cellRendererFramework: DetailsRenderer,
  ...textFilter,
});

export const getColumnDefs = (
  smallWidget: boolean,
  isCrossPatient: boolean,
  types: string[],
  minDate?: Date,
  maxDate?: Date,
) => [
  ...Patient(smallWidget, isCrossPatient),
  Date(minDate, maxDate),
  Type(types),
  Reporter(isCrossPatient),
  Details(smallWidget),
];
